import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, EmptyState } from '../ui';

/**
 * The last line of defence. Without a boundary, one thrown render error leaves a
 * blank white page - the single worst failure mode for a non-technical user,
 * because there is nothing to read out to the person who can fix it.
 *
 * It shows the message and keeps the reload within reach. It does NOT try to
 * recover: a component that threw once will usually throw again, and a "retry"
 * that loops looks like the app is broken twice.
 */
interface State {
  readonly error: Error | null;
}

export class AppErrorBoundary extends Component<{ readonly children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is the only sink until Phase 2 gives us a server to report to.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas page-gutter">
        <EmptyState
          icon="warning"
          title="Unable to show this screen."
          description={error.message}
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        />
      </div>
    );
  }
}
